import { Alert, Box, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import LogoLoader from "../components/common/LogoLoader";
import EdgeDetailOverlay from "../components/Layout/EdgeDetailOverlay";
import EntityDetailOverlay from "../components/Layout/EntityDetailOverlay";
import Sidebar from "../components/Layout/Sidebar";
import FloatingSearchBar from "../components/Map/FloatingSearchBar";
import InteractiveMap from "../components/Map/InteractiveMap";
import LoadSearchDialog from "../components/Search/LoadSearchDialog";
import SaveSearchDialog from "../components/Search/SaveSearchDialog";
import SearchFilterPanel from "../components/Search/SearchFilterPanel";
import { useAuth } from "../contexts/AuthContext";
import { useNotification } from "../contexts/NotificationContext";
import { useMapCoordinates } from "../hooks/queries/useMapCoordinates";
import {
  createSavedSearch,
  deleteSavedSearch,
  SavedSearchData,
} from "../services/savedSearchApi";
import { FacilityData, transformFacilityData } from "../types/facilityTypes";
import {
  getDbValuesFromDisplayNames as getFacilityStatusDbValues,
  getDisplayNamesFromDbValues as getFacilityStatusDisplayNames,
} from "../utils/facilityStatusMapping";
import {
  getDbValuesFromDisplayNames,
  getDisplayNamesFromDbValues,
} from "../utils/facilityTypeMapping";
import { regionMapping } from "../utils/regionMapping";
import { clearAllFiltersFromURL } from "../utils/urlFilterHelpers";

const MapView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();

  const [selectedEntity, setSelectedEntity] = useState<FacilityData | null>(
    null,
  );

  // Save/Load search state
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);

  // Create a filtered version of searchParams that excludes detail view parameters
  // This prevents filter recalculations when only detail_entity_id or detail_relationship_id changes
  const searchParamsForFilters = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete("detail_entity_id");
    params.delete("detail_relationship_id");
    return params;
  }, [
    // Only recalculate when the string representation of filter params changes
    // This prevents recalculation when detail params are added/removed
    (() => {
      const params = new URLSearchParams(searchParams);
      params.delete("detail_entity_id");
      params.delete("detail_relationship_id");
      return params.toString();
    })(),
  ]);

  // ✅ DERIVE filters from URL (single source of truth)
  const filters = useMemo(
    () => ({
      searchTerm: searchParamsForFilters.get("search") || "",
      entityType: "facilities" as const,
      regions: [],
      industries: [],
      facilityTypes: searchParamsForFilters.get("facilityTypes")
        ? getDisplayNamesFromDbValues(
            searchParamsForFilters.get("facilityTypes")!.split(","),
          )
        : [],
      organizationTypes: [],
      projectTypes: [],
      sourceTypes: [],
      labels: searchParamsForFilters.get("labels")
        ? searchParamsForFilters.get("labels")!.split(",")
        : [],
      dateRange: {
        start: searchParamsForFilters.get("dateStart") || undefined,
        end: searchParamsForFilters.get("dateEnd") || undefined,
      },
      customFilters: {
        homeCountries: searchParamsForFilters.get("homeCountries")
          ? searchParamsForFilters.get("homeCountries")!.split(",")
          : [],
        naicsCodes: searchParamsForFilters.get("naics")
          ? searchParamsForFilters.get("naics")!.split(",")
          : [],
        inputHSCodes: searchParamsForFilters.get("hsInput")
          ? searchParamsForFilters.get("hsInput")!.split(",")
          : [],
        outputHSCodes: searchParamsForFilters.get("hsOutput")
          ? searchParamsForFilters.get("hsOutput")!.split(",")
          : [],
        facilityStatuses: searchParamsForFilters.get("facilityStatuses")
          ? getFacilityStatusDisplayNames(
              searchParamsForFilters.get("facilityStatuses")!.split(","),
            )
          : [],
        facilityOwners: searchParamsForFilters.get("facilityOwners")
          ? searchParamsForFilters.get("facilityOwners")!.split(",")
          : [],
        suppliers: searchParamsForFilters.get("suppliers")
          ? searchParamsForFilters.get("suppliers")!.split(",")
          : [],
        customers: searchParamsForFilters.get("customers")
          ? searchParamsForFilters.get("customers")!.split(",")
          : [],
      },
      relationshipFilters: [],
      facilityOwnerFilter: {
        ownerCompanies: searchParamsForFilters.get("facilityOwnerCompanies")
          ? JSON.parse(searchParamsForFilters.get("facilityOwnerCompanies")!)
          : [],
        maxTiers: searchParamsForFilters.get("facilityOwnerMaxTiers")
          ? parseInt(searchParamsForFilters.get("facilityOwnerMaxTiers")!)
          : 1,
      },
      projectOrganizationFilter: {
        participantCompanies: [],
        maxTiers: 1,
      },
    }),
    [searchParamsForFilters],
  ); // Only recompute when filter params change (not detail params)

  // Convert UI filters to API filter params (used as TanStack Query key + fetch params)
  const apiFilters = useMemo(() => {
    const facilityTypeDbValues =
      filters.facilityTypes.length > 0
        ? getDbValuesFromDisplayNames(filters.facilityTypes)
        : undefined;

    const countryCodes =
      filters.customFilters.homeCountries?.length > 0
        ? regionMapping.getCountryCodesFromIds(
            filters.customFilters.homeCountries,
          )
        : undefined;

    return {
      search: filters.searchTerm || undefined,
      facility_types: facilityTypeDbValues,
      home_country:
        countryCodes && countryCodes.length > 0 ? countryCodes : undefined,
      naics:
        filters.customFilters.naicsCodes?.length > 0
          ? filters.customFilters.naicsCodes
          : undefined,
      input_hs_codes:
        filters.customFilters.inputHSCodes?.length > 0
          ? filters.customFilters.inputHSCodes
          : undefined,
      output_hs_codes:
        filters.customFilters.outputHSCodes?.length > 0
          ? filters.customFilters.outputHSCodes
          : undefined,
      facility_owners:
        filters.facilityOwnerFilter.ownerCompanies?.length > 0
          ? filters.facilityOwnerFilter.ownerCompanies.map(
              (company: any) => company.id,
            )
          : undefined,
      facility_owner_max_tiers:
        filters.facilityOwnerFilter.ownerCompanies?.length > 0
          ? filters.facilityOwnerFilter.maxTiers
          : undefined,
      facility_statuses:
        filters.customFilters.facilityStatuses?.length > 0
          ? getFacilityStatusDbValues(filters.customFilters.facilityStatuses)
          : undefined,
      suppliers:
        filters.customFilters.suppliers?.length > 0
          ? filters.customFilters.suppliers
          : undefined,
      customers:
        filters.customFilters.customers?.length > 0
          ? filters.customFilters.customers
          : undefined,
      labels: filters.labels?.length > 0 ? filters.labels : undefined,
      updated_date_start: filters.dateRange.start || undefined,
      updated_date_end: filters.dateRange.end || undefined,
    };
  }, [filters]);

  // ✅ Fetch map data via TanStack Query (replaces manual useEffect + debounce)
  const mapQuery = useMapCoordinates(apiFilters, !!user);

  // Typed so the `?? []` fallback is FacilityData[], not never[] — otherwise the
  // union breaks callback inference (TS7006) on `facilities` under TS 4.9.5.
  const facilities = useMemo<FacilityData[]>(
    () => mapQuery.data?.coordinates?.map(transformFacilityData) ?? [],
    [mapQuery.data],
  );
  const loading = mapQuery.isLoading;
  const error = mapQuery.error
    ? "Failed to load facility data. Please try again."
    : !user
      ? "Authentication required"
      : null;

  // Sync entity state with URL parameter
  useEffect(() => {
    const entityId = searchParams.get("detail_entity_id");
    if (entityId && facilities.length > 0) {
      // Only set selectedEntity if it's a facility (for map marker highlighting)
      const entity = facilities.find((e) => e.id === entityId);
      if (entity) {
        setSelectedEntity(entity);
      } else {
        // Entity exists in URL but is not a facility (Organization, Project, Source)
        // Clear selectedEntity but keep the URL parameter - EntityDetailOverlay will handle it
        setSelectedEntity(null);
      }
    } else {
      // Clear entity when parameter is removed (e.g., browser back to /map)
      setSelectedEntity(null);
    }
  }, [searchParams, facilities]);

  // ✅ Entity click handler - just updates URL
  const handleEntityClick = (entity: FacilityData) => {
    setSelectedEntity(entity);
    const params = new URLSearchParams(searchParams);
    params.delete("detail_relationship_id");
    params.set("detail_entity_id", entity.id);
    setSearchParams(params, { replace: false }); // Create history entry for entity
  };

  const handleCloseEntityDetail = () => {
    setSelectedEntity(null);
    // Create a new history entry for closing entity
    const params = new URLSearchParams(searchParams);
    params.delete("detail_entity_id");
    setSearchParams(params, { replace: false }); // Don't replace - create history entry
  };

  const handleCloseRelationshipDetail = () => {
    // Create a new history entry for closing relationship
    const params = new URLSearchParams(searchParams);
    params.delete("detail_relationship_id");
    setSearchParams(params, { replace: false }); // Don't replace - create history entry
  };

  const handleOpenInMap = (
    entityName: string,
    entityType: string,
    entityId: string,
    openInNewTab?: boolean,
  ) => {
    if (openInNewTab) {
      // Open in new tab
      let url;
      if (entityType === "organization") {
        const facilityOwnerData = JSON.stringify([
          { id: entityId, name: entityName },
        ]);
        url = `/map?facilityOwnerCompanies=${encodeURIComponent(facilityOwnerData)}&facilityOwnerMaxTiers=1`;
      } else {
        url = `/map?search=${encodeURIComponent(entityName)}`;
      }
      window.open(url, "_blank");
      return;
    }

    // ✅ Just update URL - filters will be derived from it
    if (entityType === "organization") {
      // For organizations, set as facility owner with tier 1
      const params = new URLSearchParams();
      params.set(
        "facilityOwnerCompanies",
        JSON.stringify([{ id: entityId, name: entityName }]),
      );
      params.set("facilityOwnerMaxTiers", "1");
      setSearchParams(params, { replace: false });
    } else {
      // For facilities and other entities, use search term
      const params = new URLSearchParams();
      params.set("search", entityName);
      setSearchParams(params, { replace: false });
    }
  };

  const handleMapClick = () => {
    setSelectedEntity(null);
    // Create a new history entry for closing entity via map click
    const params = new URLSearchParams(searchParams);
    params.delete("detail_entity_id");
    setSearchParams(params, { replace: false }); // Don't replace - create history entry
  };

  // ✅ Helper function to update URL params for filters
  const updateFilterInURL = useCallback(
    (filterKey: keyof typeof filters, value: any) => {
      const params = new URLSearchParams(searchParams);

      // Preserve detail overlay params
      const entityId = params.get("detail_entity_id");
      const relationshipId = params.get("detail_relationship_id");

      // Update the specific filter in URL
      if (filterKey === "searchTerm") {
        if (value) params.set("search", value);
        else params.delete("search");
      } else if (filterKey === "facilityTypes") {
        if (value.length > 0) {
          const dbValues = getDbValuesFromDisplayNames(value);
          params.set("facilityTypes", dbValues.join(","));
        } else params.delete("facilityTypes");
      } else if (filterKey === "labels") {
        if (value.length > 0) params.set("labels", value.join(","));
        else params.delete("labels");
      } else if (filterKey === "dateRange") {
        if (value.start) params.set("dateStart", value.start);
        else params.delete("dateStart");
        if (value.end) params.set("dateEnd", value.end);
        else params.delete("dateEnd");
      } else if (filterKey === "customFilters") {
        // Handle nested customFilters
        if (value.homeCountries?.length > 0)
          params.set("homeCountries", value.homeCountries.join(","));
        else params.delete("homeCountries");
        if (value.naicsCodes?.length > 0)
          params.set("naics", value.naicsCodes.join(","));
        else params.delete("naics");
        if (value.inputHSCodes?.length > 0)
          params.set("hsInput", value.inputHSCodes.join(","));
        else params.delete("hsInput");
        if (value.outputHSCodes?.length > 0)
          params.set("hsOutput", value.outputHSCodes.join(","));
        else params.delete("hsOutput");
        if (value.facilityStatuses?.length > 0) {
          const dbValues = getFacilityStatusDbValues(value.facilityStatuses);
          params.set("facilityStatuses", dbValues.join(","));
        } else params.delete("facilityStatuses");
        if (value.facilityOwners?.length > 0)
          params.set("facilityOwners", value.facilityOwners.join(","));
        else params.delete("facilityOwners");
        if (value.suppliers?.length > 0)
          params.set("suppliers", value.suppliers.join(","));
        else params.delete("suppliers");
        if (value.customers?.length > 0)
          params.set("customers", value.customers.join(","));
        else params.delete("customers");
      } else if (filterKey === "facilityOwnerFilter") {
        if (value.ownerCompanies?.length > 0) {
          params.set(
            "facilityOwnerCompanies",
            JSON.stringify(value.ownerCompanies),
          );
          params.set("facilityOwnerMaxTiers", value.maxTiers.toString());
        } else {
          params.delete("facilityOwnerCompanies");
          params.delete("facilityOwnerMaxTiers");
        }
      }

      // Restore detail overlay params
      if (entityId) params.set("detail_entity_id", entityId);
      if (relationshipId) params.set("detail_relationship_id", relationshipId);

      setSearchParams(params, { replace: true }); // Replace for filters
    },
    [searchParams, setSearchParams],
  );

  // ✅ Filter change handler - updates URL
  const handleFilterChange = useCallback(
    (filterKey: keyof typeof filters, value: any) => {
      updateFilterInURL(filterKey, value);
    },
    [updateFilterInURL],
  );

  // ✅ Clear all filters - updates URL (using shared utility)
  const clearAllFilters = useCallback(() => {
    clearAllFiltersFromURL("facilities", searchParams, setSearchParams);
  }, [searchParams, setSearchParams]);

  // ✅ Search handlers - update URL
  const handleSearchSubmit = useCallback(
    (searchTerm: string) => {
      updateFilterInURL("searchTerm", searchTerm);
    },
    [updateFilterInURL],
  );

  const handleSearchClear = useCallback(() => {
    updateFilterInURL("searchTerm", "");
  }, [updateFilterInURL]);

  // Active filter count for SearchFilterPanel
  const activeFilterCount = React.useMemo(() => {
    let count = 0;
    if (filters.searchTerm) count++;
    if (filters.facilityTypes.length > 0) count++;
    if (filters.customFilters.homeCountries?.length > 0) count++;
    if (filters.customFilters.naicsCodes?.length > 0) count++;
    if (filters.customFilters.inputHSCodes?.length > 0) count++;
    if (filters.customFilters.outputHSCodes?.length > 0) count++;
    if (filters.customFilters.facilityStatuses?.length > 0) count++;
    if (filters.customFilters.facilityOwners?.length > 0) count++;
    if (filters.customFilters.suppliers?.length > 0) count++;
    if (filters.customFilters.customers?.length > 0) count++;
    return count;
  }, [filters]);

  // Helper to extract URL params for saving (excluding detail params)
  const getSearchParamsForSave = useCallback(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      // Exclude detail view parameters
      if (key !== "detail_entity_id" && key !== "detail_relationship_id") {
        params[key] = value;
      }
    });
    return params;
  }, [searchParams]);

  // Save search handler
  const handleSaveSearch = useCallback(
    async (name: string, description?: string) => {
      try {
        await createSavedSearch({
          name,
          description,
          search_type: "facilities",
          filters: getSearchParamsForSave(),
        });
        setShowSaveDialog(false);
        showNotification(`Search "${name}" saved successfully`, "success");
      } catch (error) {
        showNotification("Failed to save search. Please try again.", "error");
        throw error;
      }
    },
    [getSearchParamsForSave, showNotification],
  );

  // Load search handler
  const handleLoadSearch = useCallback(
    (search: SavedSearchData) => {
      // Create new URLSearchParams from saved filters
      const newParams = new URLSearchParams(search.filters);

      // Preserve detail view params if they exist
      const detailEntityId = searchParams.get("detail_entity_id");
      const detailRelationshipId = searchParams.get("detail_relationship_id");
      if (detailEntityId) newParams.set("detail_entity_id", detailEntityId);
      if (detailRelationshipId)
        newParams.set("detail_relationship_id", detailRelationshipId);

      // Navigate to the new URL - this will reload the component with new filters
      navigate(`/map?${newParams.toString()}`);
      setShowLoadDialog(false);
    },
    [searchParams, navigate],
  );

  // Delete search handler
  const handleDeleteSearch = useCallback(async (id: string) => {
    try {
      await deleteSavedSearch(id);
    } catch (error) {
      throw error;
    }
  }, []);

  const handleEntityNavigation = (entityId: string) => {
    // For any entity type, just update the URL parameter and let the EntityDetailOverlay handle it
    // The overlay will fetch the entity details using the entityId
    const params = new URLSearchParams(searchParams);
    params.delete("detail_relationship_id"); // Clear relationship overlay
    params.set("detail_entity_id", entityId);
    setSearchParams(params, { replace: false }); // Don't replace - create history entry

    // Clear the current selected entity since we're navigating to a different entity
    // The new entity will be handled by the EntityDetailOverlay directly
    setSelectedEntity(null);
  };

  const handleOpenInGraph = (entityId: string) => {
    // Navigate to graph view with the selected organization as seed entity
    // Set default org structure to show parents & subsidiaries for organizations
    const graphParams = new URLSearchParams();
    graphParams.set("seed_entity", entityId);
    graphParams.set("org_structure", "parents_subsidiaries");
    navigate(`/graph?${graphParams.toString()}`);
  };

  // Error state (only show error screen, not loading screen)
  if (error) {
    return (
      <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
        <Sidebar />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#262626",
            p: 3,
          }}
        >
          <Alert
            severity="error"
            sx={{
              maxWidth: 500,
              "& .MuiAlert-message": {
                fontSize: "16px",
              },
            }}
          >
            <Typography variant="h6" gutterBottom>
              Failed to Load Map Data
            </Typography>
            {error}
          </Alert>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
      {/* Sidebar */}
      <Sidebar />

      {/* Filter Panel */}
      <SearchFilterPanel
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        onSaveSearch={() => setShowSaveDialog(true)}
        onLoadSearch={() => setShowLoadDialog(true)}
      />

      {/* Main Map Content */}
      <Box sx={{ flex: 1, position: "relative" }}>
        {/* Loading Overlay */}
        {loading && facilities.length === 0 && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <LogoLoader size="panel" label="Loading the facilities" sx={{ mb: 2 }} />
              <Typography variant="h6" color="textSecondary">
                Loading facility data...
              </Typography>
            </Box>
          </Box>
        )}

        {/* Floating Search Bar */}
        <FloatingSearchBar
          initialValue={filters.searchTerm}
          onSubmit={handleSearchSubmit}
          onClear={handleSearchClear}
          placeholder="Search facilities on the map... (Press Enter to search)"
        />

        <InteractiveMap
          entities={facilities}
          onEntityClick={handleEntityClick}
          onMapClick={handleMapClick}
          searchTerm={filters.searchTerm}
          selectedRegion={
            filters.customFilters.homeCountries?.length > 0
              ? "Custom"
              : "All Regions"
          }
          selectedTypes={filters.facilityTypes}
          selectedNaicsCodes={filters.customFilters.naicsCodes || []}
          selectedHsCodesInput={filters.customFilters.inputHSCodes || []}
          selectedHsCodesOutput={filters.customFilters.outputHSCodes || []}
          selectedFacilityOwners={filters.customFilters.facilityOwners || []}
          selectedFacilityStatuses={
            filters.customFilters.facilityStatuses || []
          }
          selectedSuppliers={filters.customFilters.suppliers || []}
          selectedCustomers={filters.customFilters.customers || []}
        />
      </Box>

      {/* Entity Detail Overlay */}
      {searchParams.get("detail_entity_id") && (
        <EntityDetailOverlay
          entityId={searchParams.get("detail_entity_id")!}
          onClose={handleCloseEntityDetail}
          onEntityClick={handleEntityNavigation}
          onOpenInGraph={handleOpenInGraph}
          onOpenInMap={handleOpenInMap}
        />
      )}

      {/* Edge Detail Overlay */}
      {searchParams.get("detail_relationship_id") && (
        <EdgeDetailOverlay
          relationshipId={searchParams.get("detail_relationship_id")!}
          onClose={handleCloseRelationshipDetail}
          onEntityClick={handleEntityNavigation}
        />
      )}

      {/* Save Search Dialog */}
      <SaveSearchDialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        onSave={handleSaveSearch}
        entityType="facilities"
        activeFilterCount={activeFilterCount}
      />

      {/* Load Search Dialog */}
      <LoadSearchDialog
        open={showLoadDialog}
        onClose={() => setShowLoadDialog(false)}
        onLoad={handleLoadSearch}
        onDelete={handleDeleteSearch}
        entityType="facilities"
      />
    </Box>
  );
};

export default MapView;
